import React, { useEffect, useState, useRef } from "react";
import { convertToMinutesAndSeconds, getCookie } from "../../../../helper/commonFunction";
import {
  DocumentIcon,
  ThumbIcon,
  TimeIcon,
  UserGroupIcon,
} from "../../../../helper/HwIcon";
import { ChevronLeftIcon } from "../../../../helper/HwIcon";
import { HotelCheckoutTabs } from "../HotelCheckoutConstants.js";
import { useNavigate } from "react-router-dom";
import CommonPopup from "../../../common-popup/CommonPopup.js";

const statusData = {
  CONFIRMED: {
    text: "Booking Confirmed",
    msg: "Thank you! Your hotel booking is confirmed and a confirmation is on its way to your email.",
  },
  STATUS_REQUESTED: {
    text: "Status Requested",
    msg: "Booking requested — please contact our support team for further action.",
  },
  ON_REQUEST: {
    text: "On Request",
    msg: "Booking is on request, waiting for confirmation from the hotel.",
  },
  CANCELLATION_CONFIRMED: { text: "Booking Cancelled", msg: "Your hotel booking has been cancelled." },
  CANCELATION_CONFIRMED: { text: "Booking Cancelled", msg: "Your hotel booking has been cancelled." },
  CANCELATION_REQUEST: {
    text: "Cancellation Requested",
    msg: "Booking is requested for cancellation, waiting for confirmation from the hotel.",
  },
};

export const tabs = {
  [HotelCheckoutTabs.GUEST_DETAILS]: { tab: HotelCheckoutTabs.GUEST_DETAILS, step: 1, label: "Hotel & Guest Details", icon: () => <UserGroupIcon /> },
  [HotelCheckoutTabs.REVIEW]: { tab: HotelCheckoutTabs.REVIEW, step: 2, label: "Review & Payment", icon: () => <DocumentIcon /> },
  [HotelCheckoutTabs.CONFIRM]: { tab: HotelCheckoutTabs.CONFIRM, step: 3, label: "Booking Confirmation", icon: () => <ThumbIcon /> },
};

const fifteenMinutes = 60 * 15;

const STEPS = [
  { n: 1, tab: HotelCheckoutTabs.GUEST_DETAILS, label: "Guest details" },
  { n: 2, tab: HotelCheckoutTabs.REVIEW, label: "Review & Payment" },
];

const Ico = {
  check: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M5 12.5l4.5 4.5L19 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
  ),
  bigCheck: (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none"><path d="M5 12.5l4.5 4.5L19 7" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
  ),
  clock: (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.9" /><path d="M12 7.5V12l3 2" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" /></svg>
  ),
  cross: (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none"><path d="M7 7l10 10M17 7L7 17" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" /></svg>
  ),
};

export const HotelCheckooutTopTabs = ({ currentTab, bookingId, bookingStatus, onBack }) => {
  const [show, setShow] = useState(false);
  const navigate = useNavigate();

  if (currentTab === HotelCheckoutTabs.CONFIRM) {
    const isCancel = bookingStatus === "CANCELLATION_CONFIRMED" || bookingStatus === "CANCELATION_CONFIRMED" || bookingStatus === "CANCELATION_REQUEST";
    const tone = isCancel ? "is-cancel-hotel" : bookingStatus === "CONFIRMED" ? "is-confirm-hotel" : "is-pending";
    const icon = isCancel ? Ico.cross : bookingStatus === "CONFIRMED" ? Ico.bigCheck : Ico.clock;
    return (
      <div className={`hw-cfhero ${tone}`}>
        <div className="hw-exchk--heroInner">
          <button
            type="button"
            className="hw-exchk--back hw-cfhero--back"
            onClick={() => navigate("/hotels/search")}
          >
            <ChevronLeftIcon /> Back to search
          </button>
          <div className="hw-cfhero--bar">
            <div className="hw-cfhero--left">
              <span className="hw-cfhero--badge">{icon}</span>
              <div>
                <h1 className="hw-cfhero--title">{statusData[bookingStatus]?.text || "Booking"}</h1>
                <p className="hw-cfhero--sub">{statusData[bookingStatus]?.msg}</p>
              </div>
            </div>
            <div className="hw-cfhero--id">
              <span className="hw-cfhero--idVal">Booking ID : {bookingId}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const cur = currentTab === HotelCheckoutTabs.REVIEW ? 2 : 1;

  return (
    <div className="hw-exchk--hero hw-hotel--hero">
      <CommonPopup
        show={show}
        title={"Notification"}
        setShow={() => {
          const url = getCookie("hotelURL");
          navigate(`/hotels/details/${JSON.parse(url)}`);
          setShow(false);
        }}
      >
        <p>Your booking session time has ended to ensure availability and accurate pricing. <br /> Please start a new search to proceed.</p>
      </CommonPopup>
      <div className="hw-exchk--heroInner">
        <button type="button" className="hw-exchk--back" onClick={onBack}>
          <ChevronLeftIcon /> Back
        </button>
        <div className="hw-exchk--heroBar">
          <h1 className="hw-exchk--heroTitle">Complete Your Booking</h1>
          <div className="hw-hotel--heroTimer">
            <TimeIcon /> Session expires in <Timer startFrom={fifteenMinutes} setShow={setShow} />
          </div>
          <ol className="hw-exchk--steps">
            {STEPS.map((st) => (
              <li key={st.n} className={`hw-exchk--step ${cur === st.n ? "is-active" : ""} ${cur > st.n ? "is-done" : ""}`}>
                <span className="hw-exchk--stepNum">{cur > st.n ? Ico.check : st.n}</span>
                <span className="hw-exchk--stepLabel">{st.label}</span>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
};

export const Timer = ({ startFrom, setShow }) => {
  const [timeRemaining, setTimeRemaining] = useState(0);
  const hasRedirectedRef = useRef(false);
  useEffect(() => {
    setTimeRemaining(startFrom);
    hasRedirectedRef.current = false;
    const interval = setInterval(() => {
      setTimeRemaining((t) => {
        if (t <= 1) {
          clearInterval(interval);
          if (!hasRedirectedRef.current) {
            hasRedirectedRef.current = true;
            window.scrollTo({ top: 0, behavior: "instant" });
            setTimeout(() => setShow(true), 100);
          }
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [startFrom]);
  const displayTime = timeRemaining <= 0 ? 0 : timeRemaining;
  return <>{convertToMinutesAndSeconds(displayTime)}</>;
};
